import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Badge, Button, Checkbox, Select } from "nlib/ui";
import { checkIsBusinessUser, getUserData } from "selectors/user";
import { checkTasksFetching, getTasksData } from "selectors/tasks";
import { getActiveUsersData } from "selectors/organizations";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { marked } from "marked";
import { useDispatch, useSelector } from "react-redux";
import Activity from "nlib/common/Activity";
import Arrays from "utils/Arrays";
import Attachments from "nlib/common/Attachments";
import AutoTaskContent from "./lib/AutoTaskContent";
import CommentTargetTypes from "const/CommentTargetTypes";
import CommentTypes from "const/CommentTypes";
import CommentsActions from "actions/CommentsActions";
import CustomDropArea from "nlib/common/CustomDropArea";
import DateTimeFormats from "const/DateTimeFormats";
import FormLabel from "../FormLabel";
import IntegrationServices from "const/IntegrationServices";
import Objects from "utils/Objects";
import Preloader from "nlib/ui/Preloader";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import SelectUserAutocomplete from "../TaskForm/lib/FormContent/lib/SelectUserAutocomplete";
import SideBar, { SideBarContent, SideBarFooter, SideBarHeader } from "../SideBar";
import TaskRepeatOptions from "nlib/common/TaskRepeatOptions";
import TaskStatuses from "const/TaskStatuses";
import TaskTypes from "const/TaskTypes";
import TasksActions from "actions/TasksActions";
import Texts from "utils/Texts";
import UserRoles from "const/UserRoles";
import classNames from "classnames";
import moment from "moment";
import purify from "dompurify";
import useEnvVars from "hooks/useEnvVars";
import useShowCommonModal from "hooks/useShowCommonModal";
import useTaskContent from "hooks/useTaskContent";

const END_TYPES = {
  DATE: "date",
  OCCURRENCES: "occurrences"
};

const ROLES_ALLOWED_TO_ASSIGN_TASKS = [
  UserRoles.ACCOUNTANT_ADMIN.roleId,
  UserRoles.ACCOUNTANT_MANAGER.roleId
];

const TaskContent = ({ className, scrollToActivity, taskId, mobile }) => {
  const [, setEnvVars] = useEnvVars();

  const textContainerRef = useRef();

  const activitySectionRef = useRef();

  const dispatch = useDispatch();

  const showCommonModal = useShowCommonModal();

  const { uiTexts, messages } = useSelector(getTextsData);

  const tasksFetching = useSelector(checkTasksFetching);

  const businessUser = useSelector(checkIsBusinessUser);

  const allTasksData = useSelector(getTasksData);

  const usersData = useSelector(getActiveUsersData);

  const currentTaskData = useMemo(() => {
    return Arrays.findById(allTasksData, taskId) || {};
  }, [allTasksData, taskId]);

  const { extraData: { integrationService } = {} } = useSelector(getSelectedBusinessData);

  const [showAllAttachments, setShowAllAttachments] = useState(false);

  const [showTextExpander, setShowTextExpander] = useState(false);

  const [showAllText, setShowAllText] = useState(false);

  const [activityLoaded, setActivityLoaded] = useState(!taskId);

  const [repeatableData, setRepeatableData] = useState();

  const [content, setContent] = useState(null);

  const { status, type = TaskTypes.CUSTOM } = content || {};

  const autoTask = type && (type !== TaskTypes.CUSTOM);

  const { shortLabel: serviceName } = IntegrationServices.getByValue(integrationService) || {};

  const {
    completed,
    dueDateContent,
    dueDateStatus,
    createdByContent,
    createdAtContent,
    startDateContent,
    taskForContent
  } = useTaskContent({
    ...content,
    createdBy: usersData.find(({ id }) => id === content?.createdBy),
    targetUser: usersData.find(({ id }) => id === content?.targetUser)
  });

  const { id: userId, role: userRole } = useSelector(getUserData);

  const canBeManaged = useMemo(() => {
    return !autoTask
      && (userRole === UserRoles.ACCOUNTANT_ADMIN.roleId || content?.createdBy === userId);
  }, [content?.createdBy, userRole, userId, autoTask]);

  const statusOptions = useMemo(() => {
    return [
      { value: TaskStatuses.TO_DO, label: uiTexts[TaskStatuses.TO_DO] },
      { value: TaskStatuses.IN_PROGRESS, label: uiTexts[TaskStatuses.IN_PROGRESS] },
      { value: TaskStatuses.ON_HOLD, label: uiTexts[TaskStatuses.ON_HOLD] },
      {
        disabled: autoTask,
        value: TaskStatuses.COMPLETED,
        label: uiTexts[TaskStatuses.COMPLETED]
      }
    ];
  }, [uiTexts, autoTask]);

  const repeatOptionsHasChanges = useMemo(() => {
    const { id: prevId, ...prevData } = content?.repeatableData || {};

    const { id: nextId, ...nextData } = repeatableData || {};

    return !Objects.checkDeepEqualityByProps(prevData, nextData);
  }, [content?.repeatableData, repeatableData]);

  const monthYearStartDateText = useMemo(() => {
    return content?.startDate ? moment(content.startDate).format(DateTimeFormats.MONTH_AND_YEAR_TEXT) : "";
  }, [content?.startDate]);

  const onClose = useCallback(() => {
    setEnvVars({ editTask: null });
  }, [setEnvVars]);

  const fetchData = useCallback(async() => {
    setActivityLoaded(false);

    const [response] = await Promise.all([
      dispatch(TasksActions.fetchTask(taskId)),
      dispatch(CommentsActions.fetchComments(CommentTargetTypes.TASKS, taskId, true))
    ]);

    if (response) {
      setContent(response);
      setRepeatableData(response?.repeatableData);
    } else {
      onClose();
    }
    setActivityLoaded(true);
  }, [dispatch, taskId, onClose]);

  const editTask = useCallback(async(payload, silentUpdate = false) => {
    setContent((prev) => ({ ...prev, ...payload }));

    const response = await dispatch(TasksActions.editTask(taskId, payload, true, silentUpdate));

    if (!response) return null;
    if (payload.files?.length) {
      const comment = `${messages.filesAttached}: ${payload.files.map(({ name }) => name).join(", ")}`;

      await dispatch(
        CommentsActions.addNewComment(CommentTypes.COMMENT, comment, [], false, response.id, CommentTargetTypes.TASKS)
      );
    }
    setContent(response);
    dispatch(TasksActions.fetchTasksList(false, true));

    return response;
  }, [taskId, messages, dispatch]);

  const handleBackClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleStatusChange = useCallback(async(newStatus) => {
    if (status === newStatus) return;

    const result = await editTask({ status: newStatus }, true);

    if (result) {
      const message = Texts.replaceVars(messages.taskStatusChangedTo, { status: uiTexts[newStatus] });

      dispatch(CommentsActions.addNewComment(CommentTypes.COMMENT, message, [], false, taskId, CommentTargetTypes.TASKS));
    }
  }, [status, dispatch, editTask, messages, taskId, uiTexts]);

  const handleFilesChange = useCallback(async(files) => {
    setShowAllAttachments(true);
    await editTask({ files });
  }, [editTask]);

  const handleCompletedButtonClick = useCallback(() => {
    handleStatusChange(completed ? TaskStatuses.TO_DO : TaskStatuses.COMPLETED);
  }, [handleStatusChange, completed]);

  const handleRepeatOptionsChange = useCallback((data) => {
    if (!data.enabled && content?.repeatableData?.enabled) {
      editTask({ repeatableData: data });
    }
    setRepeatableData(data);
  }, [editTask, content?.repeatableData?.enabled]);

  const handleRepeatOptionsCancel = useCallback(() => {
    setRepeatableData(content?.repeatableData);
  }, [content?.repeatableData]);

  const handleRepeatOptionsSave = useCallback(async() => {
    const response = await editTask({ repeatableData });

    if (response) setRepeatableData(response?.repeatableData);
  }, [editTask, repeatableData]);

  const handleTextExpanderClick = useCallback(() => {
    setShowAllText((prev) => !prev);
  }, []);

  const handleTargetUserChange = useCallback((value) => {
    if (value && (content?.targetUser !== value)) {
      editTask({ targetUser: value });
    }
  }, [editTask, content?.targetUser]);

  const handleDeleteTask = useCallback(async() => {
    const result = await showCommonModal({ confirm: true, text: messages.taskDeleteConfirm });

    if (result) {
      setEnvVars({ editTask: null });
      await dispatch(TasksActions.deleteTask(taskId));
    }
  }, [showCommonModal, messages.taskDeleteConfirm, dispatch, taskId, setEnvVars]);

  const handleResetTargetUser = useCallback(() => {
    editTask({ targetUser: null });
  }, [editTask]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [taskId, fetchData, dispatch]);
  useEffect(() => {
    if (textContainerRef.current) {
      setShowTextExpander(textContainerRef.current.clientHeight < textContainerRef.current.scrollHeight);
    }
  }, [content, activityLoaded]);
  useEffect(() => {
    if (scrollToActivity && activitySectionRef.current) {
      activitySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollToActivity, content, activityLoaded]);
  useEffect(() => {
    if (currentTaskData?.status) {
      setContent((prev) => ({ ...prev, status: currentTaskData.status }));
    }
  }, [currentTaskData?.status]);
  if (!content || !activityLoaded) {
    return (
      <SideBar className={classNames(Css.taskContent, className)}>
        <SideBarHeader onCloseClick={handleBackClick}>{uiTexts.task}</SideBarHeader>
        <SideBarContent><Preloader /></SideBarContent>
      </SideBar>
    );
  }

  return (
    <SideBar className={classNames(Css.taskContent, className)}>
      <SideBarHeader onCloseClick={handleBackClick}>{uiTexts.task}</SideBarHeader>
      <SideBarContent>
        <div className={Css.contentWrap}>
          <div className={Css.col}>
            <FormLabel>{uiTexts.status}</FormLabel>
            {(!businessUser || autoTask)
              ? (
                <Select
                  disabled={tasksFetching || (autoTask && completed)}
                  className={Css.statusSelect}
                  dropDownClassName={Css.dropDownClassName}
                  value={status}
                  options={statusOptions}
                  onChange={handleStatusChange} />
              )
              : (
                <Button
                  large block outline
                  disabled={tasksFetching}
                  className={Css.completeButton}
                  onClick={handleCompletedButtonClick}>
                  <Checkbox toggle checked={completed} />
                  <span>{completed ? uiTexts.unmarkAsComplete : uiTexts.markAsComplete}</span>
                </Button>
              )}
            {autoTask
              ? (
                <div className={Css.title}>
                  {({
                    [TaskTypes.TRANSACTIONS]: Texts.replaceVars(
                      uiTexts.categorizeTransactionsForDate,
                      { date: monthYearStartDateText }
                    ),
                    [TaskTypes.DOCUMENTS]: Texts.replaceVars(
                      uiTexts.reviewUploadedDocumentsForDate,
                      { date: monthYearStartDateText }
                    ),
                    [TaskTypes.AUDIT]: Texts.replaceVars(
                      uiTexts.resolveCleanUpIssuesForDate,
                      { date: monthYearStartDateText }
                    ),
                    [TaskTypes.BANK_FEEDS]: Texts.replaceVars(
                      uiTexts.renewServiceBankConnections,
                      { serviceName }
                    )
                  })[content.type] || ""}
                </div>
              )
              : <FormLabel>{uiTexts.fullDescription}</FormLabel>}
            {autoTask
              ? (
                <div className={Css.text}>
                  {({
                    [TaskTypes.TRANSACTIONS]: messages.uncategorizedAutoTaskDescription,
                    [TaskTypes.DOCUMENTS]: messages.ocrAiAutoTaskDescription,
                    [TaskTypes.AUDIT]: messages.cleanUpAutoTaskDescription,
                    [TaskTypes.BANK_FEEDS]: messages.bankFeedsAutoTaskDescription
                  })[content.type] || ""}
                </div>
              )
              : (
                <div className={classNames(Css.text, showAllText && Css.showAllText)} ref={textContainerRef}>
                  <div
                    className={Css.markdown}
                    dangerouslySetInnerHTML={{ __html: purify.sanitize(marked(content.text || "")) }} />
                </div>
              )}
            {showTextExpander && (
              <div className={Css.showMoreText} onClick={handleTextExpanderClick}>
                <div>{showAllText ? uiTexts.showLess : uiTexts.showMore}</div>
              </div>
            )}
            {!businessUser && (
              <>
                <FormLabel>{uiTexts.taskFor}</FormLabel>
                <div className={Css.taskFor}>
                  <SelectUserAutocomplete
                    disabled={tasksFetching}
                    value={content.targetUser}
                    allowedRoles={[TaskTypes.TRANSACTIONS, TaskTypes.DOCUMENTS, TaskTypes.AUDIT]
                      .includes(content.type) && ROLES_ALLOWED_TO_ASSIGN_TASKS}
                    onChange={handleTargetUserChange} />
                  {!!content.targetUser && (
                    <Button
                      outline large danger
                      className={Css.resetButton}
                      data-tooltip={uiTexts.unassignUser}
                      onClick={handleResetTargetUser}>
                      <Icons.X />
                    </Button>
                  )}
                </div>
              </>
            )}
            {((autoTask && !businessUser) || (content.type === TaskTypes.BANK_FEEDS))
              ? (
                <AutoTaskContent
                  type={content.type}
                  startDate={content.startDate} />
              )
              : (
                <div className={Css.header}>
                  <div className={Css.info}>
                    {!!startDateContent && (
                      <div className={Css.infoItem}>
                        <div className={Css.infoItemTitle}>{uiTexts.startDate}</div>
                        <div className={Css.infoItemValue} title={startDateContent}>
                          <Badge outline>{startDateContent}</Badge>
                        </div>
                      </div>
                    )}

                    {dueDateContent && (
                      <div className={Css.infoItem}>
                        <div className={Css.infoItemTitle}>{uiTexts.deadline}</div>
                        <div className={Css.infoItemValue}>
                          <Badge theme={dueDateStatus}>{dueDateContent}</Badge>
                        </div>
                      </div>
                    )}

                    {businessUser && (
                      <div className={Css.infoItem}>
                        <div className={Css.infoItemTitle}>{uiTexts.taskFor}</div>
                        <div className={Css.infoItemValue} title={taskForContent}>
                          <Icons.User />
                          <span>{taskForContent}</span>
                        </div>
                      </div>
                    )}

                    <div className={Css.infoItem}>
                      <div className={Css.infoItemTitle}>{uiTexts.created}</div>
                      <div className={Css.infoItemValue}>
                        <span>{createdAtContent}</span>
                      </div>
                    </div>

                    <div className={Css.infoItem}>
                      <div className={Css.infoItemTitle}>{uiTexts.createdBy}</div>
                      <div className={Css.infoItemValue}>
                        <span>{createdByContent || uiTexts.system}</span>
                      </div>
                    </div>

                  </div>
                </div>
              )}
            {!businessUser && !autoTask && (
              <div className={Css.repeatOptions}>
                <TaskRepeatOptions
                  disabled={tasksFetching}
                  startDate={content.startDate}
                  value={repeatableData}
                  onChange={handleRepeatOptionsChange} />
                {repeatableData?.enabled && repeatOptionsHasChanges && (
                  <div className={Css.actions}>
                    <Button
                      outline
                      disabled={tasksFetching}
                      onClick={handleRepeatOptionsCancel}>{uiTexts.cancel}</Button>
                    <Button
                      primary
                      disabled={tasksFetching || (
                        repeatableData.endType === END_TYPES.OCCURRENCES
                          ? !repeatableData.endAfterRepetitions
                          : !repeatableData.endAfterDate
                      )}
                      onClick={handleRepeatOptionsSave}>
                      {uiTexts.save}
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className={Css.attachmentsArea}>
              <FormLabel>{uiTexts.attachedFiles}</FormLabel>
              {!!content.attachments?.length && (
                <Attachments
                  className={Css.attachments}
                  showAll={showAllAttachments || undefined}
                  attachments={content.attachments} />
              )}
              <CustomDropArea
                className={Css.dropArea}
                disabled={tasksFetching}
                onChange={handleFilesChange} />
            </div>
          </div>
          <div className={classNames(Css.col, Css.activityCol)} ref={activitySectionRef}>
            <FormLabel>{uiTexts.activity}</FormLabel>
            <Activity
              mobile={mobile}
              dataLoadedInitial
              disabled={tasksFetching}
              className={classNames(Css.activity, Css.activityPanel)}
              contentClassName={mobile ? Css.activityContent : undefined}
              type={CommentTargetTypes.TASKS}
              itemId={taskId} />
          </div>
        </div>
      </SideBarContent>
      {canBeManaged && (
        <SideBarFooter>
          <Button
            danger outline large
            icon={Icons.TrashSimple}
            onClick={handleDeleteTask}>{uiTexts.deleteTask}</Button>
        </SideBarFooter>
      )}
    </SideBar>
  );
};

export default React.memo(TaskContent);
